/**
 * The measurement block at the top of the rail.
 *
 * These are the numbers the photographer judges the frame by, and they are the
 * same numbers handed to the planner: one measurement of one frame, shown
 * rather than hidden. Every scope is backed by the readout strip so nothing
 * here depends on reading a colour off a graph.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useStore } from '../state/store.ts';
import { Segmented } from './controls/Panel.tsx';
import { Tip } from './controls/Tooltip.tsx';

type ScopeView = 'histogram' | 'parade' | 'tonal';

const VIEWS: { value: ScopeView; label: string }[] = [
  { value: 'histogram', label: 'Histogram' },
  { value: 'parade', label: 'Parade' },
  { value: 'tonal', label: 'Tonal' },
];

/** Mirrors the channel inks used by the curve editor and the design system. */
const CHANNEL_INK = { red: '#e2564a', green: '#62b06a', blue: '#5a86e0' } as const;
const LUMA_INK = '#ffedd7';
const GRID_INK = '#2a231d';

/** Clipping above this share of the frame is called out rather than just shown. */
const CLIP_WARN = 0.005;

const ZONE_INK: Record<'shadows' | 'midtones' | 'highlights', string> = {
  shadows: '#40372e',
  midtones: '#6c5f51',
  highlights: '#b9a894',
};

function percent(value: number, decimals = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/** Horizontal padding on `.panel-body`, which the measured box includes. */
const BODY_PADDING = 28;

/**
 * Keeps the plot in proportion as the rail is dragged. A fixed height turns
 * into a letterbox strip at 560px and a cramped one at 260px.
 */
function plotHeight(width: number, view: ScopeView): number {
  const tall = view === 'histogram';
  const low = tall ? 104 : 92;
  if (width <= 0) return low + 12;
  return Math.round(Math.min(tall ? 172 : 150, Math.max(low, width * (tall ? 0.44 : 0.38))));
}

/**
 * Tracks an element's CSS-pixel box. Takes the element itself, not a ref, so
 * it re-attaches when a view swaps the canvas out and back in. Measured off the
 * bounding rect: the rail lands on fractional widths, and rounding the box
 * before scaling it by the device ratio is what makes a canvas go soft.
 */
function useElementSize<T extends HTMLElement>(element: T | null) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize((previous) =>
        previous.width === rect.width && previous.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return size;
}

/**
 * Scale for a histogram plot. The end bins collect every clipped pixel, so a
 * blown highlight would otherwise squash the rest of the frame flat.
 */
function interiorPeak(...series: number[][]): number {
  let peak = 0;
  for (const values of series) {
    for (let i = 1; i < values.length - 1; i += 1) peak = Math.max(peak, values[i]);
  }
  if (peak > 0) return peak;
  for (const values of series) for (const value of values) peak = Math.max(peak, value);
  return peak || 1;
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  values: number[],
  peak: number,
  left: number,
  top: number,
  width: number,
  height: number,
) {
  ctx.beginPath();
  ctx.moveTo(left, top + height);
  values.forEach((value, index) => {
    const x = left + ((index + 0.5) / values.length) * width;
    const y = top + height - Math.min(1, value / peak) * height;
    ctx.lineTo(x, y);
  });
  ctx.lineTo(left + width, top + height);
  ctx.closePath();
}

export function Scopes() {
  const stats = useStore((state) => state.stats);
  const showClipping = useStore((state) => state.showClipping);
  const toggleClipping = useStore((state) => state.toggleClipping);
  const [view, setView] = useState<ScopeView>('histogram');

  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const bodySize = useElementSize(body);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const size = useElementSize(canvas);

  const draw = useCallback(() => {
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !stats || size.width === 0 || size.height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const deviceWidth = Math.max(1, Math.round(size.width * dpr));
    const deviceHeight = Math.max(1, Math.round(size.height * dpr));
    if (canvas.width !== deviceWidth) canvas.width = deviceWidth;
    if (canvas.height !== deviceHeight) canvas.height = deviceHeight;

    const w = size.width;
    const h = size.height;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const { red, green, blue } = stats.histograms;

    if (view === 'histogram') {
      const peak = interiorPeak(red, green, blue, stats.luminance);

      ctx.strokeStyle = GRID_INK;
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i += 1) {
        const x = Math.round((i / 4) * w) + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }

      // Additive blend: where all three channels agree the result reads white,
      // which is exactly how a neutral frame should look.
      ctx.globalCompositeOperation = 'lighter';
      ([['red', red], ['green', green], ['blue', blue]] as const).forEach(([name, values]) => {
        tracePath(ctx, values, peak, 0, 0, w, h);
        ctx.fillStyle = CHANNEL_INK[name];
        ctx.globalAlpha = 0.42;
        ctx.fill();
      });
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      tracePath(ctx, stats.luminance, peak, 0, 0, w, h);
      ctx.strokeStyle = LUMA_INK;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = 1;
      return;
    }

    if (view === 'parade') {
      const peak = interiorPeak(red, green, blue);
      const columnWidth = w / 3;
      ([['red', red, 'R'], ['green', green, 'G'], ['blue', blue, 'B']] as const).forEach(
        ([name, values, letter], index) => {
          const left = index * columnWidth;
          tracePath(ctx, values, peak, left + 3, 2, columnWidth - 6, h - 2);
          ctx.fillStyle = CHANNEL_INK[name];
          ctx.globalAlpha = 0.34;
          ctx.fill();
          ctx.globalAlpha = 0.95;
          ctx.lineWidth = 1;
          ctx.strokeStyle = CHANNEL_INK[name];
          ctx.stroke();
          ctx.globalAlpha = 1;

          if (index > 0) {
            ctx.strokeStyle = GRID_INK;
            ctx.beginPath();
            ctx.moveTo(Math.round(left) + 0.5, 0);
            ctx.lineTo(Math.round(left) + 0.5, h);
            ctx.stroke();
          }

          ctx.fillStyle = CHANNEL_INK[name];
          ctx.font = "9px ui-monospace, 'SF Mono', Menlo, monospace";
          ctx.textBaseline = 'top';
          ctx.fillText(letter, left + 4, 3);
        },
      );
    }
  }, [canvas, size, stats, view]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (!stats) {
    return (
      <section className="panel">
        <div className="panel-body" ref={setBody} style={{ paddingTop: 12, gap: 8 }}>
          <div
            className="scope"
            style={{ height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span className="muted" style={{ fontSize: 11 }}>
              Open a photo and the scopes fill in.
            </span>
          </div>
        </div>
      </section>
    );
  }

  const highlightClip = stats.clippedHighlights;
  const shadowClip = stats.clippedShadows;
  const { r, g, b } = stats.neutralBalance;
  const measuredNeutral = g > 0;
  const warmth = measuredNeutral ? (r - b) / g : 0;
  const warmthWord = Math.abs(warmth) < 0.02 ? 'neutral' : warmth > 0 ? 'warm' : 'cool';

  const summary =
    `Black point ${percent(stats.blackPoint)}, white point ${percent(stats.whitePoint)}, ` +
    `mean luminance ${percent(stats.meanLuminance)}. ` +
    `Highlights clipped ${percent(highlightClip, 1)}, shadows clipped ${percent(shadowClip, 1)}.`;

  const canvasLabel =
    view === 'histogram'
      ? `Overlaid red, green and blue histograms with a luminance outline. ${summary}`
      : `Red, green and blue histograms side by side. Channel means: red ${percent(
          stats.channels.red.mean,
        )}, green ${percent(stats.channels.green.mean)}, blue ${percent(stats.channels.blue.mean)}.`;

  const castTip = measuredNeutral
    ? `The bright near-neutral pixels average ${r.toFixed(3)} : ${g.toFixed(3)} : ${b.toFixed(
        3,
      )}. Warm means red is leading, cool means blue is.`
    : 'Too few bright near-neutral pixels in this frame to read a cast.';

  const zones = [
    { key: 'shadows' as const, name: 'Shadows' },
    { key: 'midtones' as const, name: 'Midtones' },
    { key: 'highlights' as const, name: 'Highlights' },
  ];

  return (
    <section className="panel">
      <div className="panel-body" ref={setBody} style={{ paddingTop: 12, gap: 8 }}>
        <Segmented value={view} options={VIEWS} onChange={setView} ariaLabel="Scope" />

        {view === 'tonal' ? (
          <div className="scope" style={{ padding: 10 }}>
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                height: 16,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${GRID_INK}`,
              }}
            >
              {zones.map((zone) => (
                <div
                  key={zone.key}
                  style={{
                    width: `${stats.zones[zone.key].share * 100}%`,
                    background: ZONE_INK[zone.key],
                  }}
                />
              ))}
            </div>
            <ul
              style={{
                listStyle: 'none',
                margin: '9px 0 0',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              {zones.map((zone) => (
                <li key={zone.key} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: ZONE_INK[zone.key],
                      flex: '0 0 auto',
                    }}
                  />
                  <span className="label" style={{ flex: 1 }}>
                    {zone.name}
                  </span>
                  <span className="value" style={{ fontSize: 11 }}>
                    {percent(stats.zones[zone.key].share, 1)}
                  </span>
                  <span className="muted" style={{ fontSize: 10, width: 62, textAlign: 'right' }}>
                    mean {percent(stats.zones[zone.key].mean)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="muted" style={{ fontSize: 10, paddingTop: 8 }}>
              Split at 25% and 70% luminance.
            </div>
          </div>
        ) : (
          <div className="scope" style={{ height: plotHeight(bodySize.width - BODY_PADDING, view) }}>
            <canvas ref={setCanvas} role="img" aria-label={canvasLabel} />
            <ClipButton
              side="left"
              label="Shadows"
              short="Lo"
              share={shadowClip}
              on={showClipping}
              onToggle={toggleClipping}
            />
            <ClipButton
              side="right"
              label="Highlights"
              short="Hi"
              share={highlightClip}
              on={showClipping}
              onToggle={toggleClipping}
            />
          </div>
        )}

        <div className="scope-readout">
          <Tip title="Black" body="Where the darkest 1% of the frame sits. Above zero means lifted blacks.">
            <b>Black</b> {percent(stats.blackPoint)}
          </Tip>
          <Tip title="White" body="Where the brightest 1% sits. Near 100% and you are on the edge of clipping.">
            <b>White</b> {percent(stats.whitePoint)}
          </Tip>
          <Tip title="Mean" body="Average brightness of the whole frame.">
            <b>Mean</b> {percent(stats.meanLuminance)}
          </Tip>
        </div>
        <div className="scope-readout" style={{ paddingTop: 0 }}>
          <Tip
            title="Highlight clipping"
            body="Share of the frame with all three channels at the top. No detail left up there."
          >
            <b>Hi</b>{' '}
            <span className={highlightClip > CLIP_WARN ? 'clip-warn' : undefined}>
              {percent(highlightClip, 1)}
            </span>
          </Tip>
          <Tip
            title="Shadow clipping"
            body="Share of the frame with all three channels at the bottom. Nothing to recover there."
          >
            <b>Lo</b>{' '}
            <span className={shadowClip > CLIP_WARN ? 'clip-warn' : undefined}>
              {percent(shadowClip, 1)}
            </span>
          </Tip>
          <Tip title="Colour cast" body={castTip}>
            <b>Cast</b>{' '}
            {measuredNeutral
              ? `${warmthWord}${warmthWord === 'neutral' ? '' : ` ${warmth > 0 ? '+' : '−'}${Math.round(
                  Math.abs(warmth) * 100,
                )}%`}`
              : 'not read'}
          </Tip>
        </div>
      </div>
    </section>
  );
}

interface ClipButtonProps {
  side: 'left' | 'right';
  label: string;
  /** Two-letter face for the corner; the accessible name carries the full text. */
  short: string;
  share: number;
  on: boolean;
  onToggle: () => void;
}

/**
 * Both corners toggle the same overlay, since the store carries one clipping
 * flag, so each says which end it is reporting rather than implying two
 * switches.
 */
function ClipButton({ side, label, short, share, on, onToggle }: ClipButtonProps) {
  const description = `${label} clipped on ${percent(share, 1)} of the frame. Click to show it on the picture.`;
  return (
    <button
      type="button"
      className={`btn tiny${on ? ' active' : ''}`}
      aria-pressed={on}
      aria-label={description}
      onClick={onToggle}
      title={description}
      style={{
        position: 'absolute',
        top: 4,
        left: side === 'left' ? 4 : undefined,
        right: side === 'right' ? 4 : undefined,
        background: on ? undefined : 'rgba(10, 5, 3, 0.72)',
      }}
    >
      <span className="label" style={{ fontSize: 9, color: 'inherit' }}>
        {short}
      </span>
      <span className={share > CLIP_WARN ? 'clip-warn' : undefined}>{percent(share, 1)}</span>
    </button>
  );
}
